import {ApplicationRef, computed, DestroyRef, inject, Injectable, signal, Signal, WritableSignal} from "@angular/core";
import {debounceTime, fromEvent} from "rxjs";
import {ConfigurationModel, ParamConfig} from "../configuration/configuration.model";
import {onEach} from '../utils/observable-utils';

export enum PageMode {
    SMALL_MOBILE,
    MEDIUM_MOBILE,
    MOBILE,
    NARROW_DESKTOP,
    BASIC_DESKTOP,
    WIDE_DESKTOP,
    WIDER_DESKTOP,
    WIDEST_DESKTOP,
}

@Injectable({providedIn: "root"})
export class ResponsiveService {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private applicationRef: ApplicationRef = inject(ApplicationRef);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);

    private readonly _isIos: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isIos: Signal<boolean> = this._isIos.asReadonly();
    private readonly _isIpad: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isIpad: Signal<boolean> = this._isIpad.asReadonly();
    private readonly _isAndroid: WritableSignal<boolean> = signal<boolean>(false);
    // noinspection JSUnusedGlobalSymbols
    public readonly isAndroid: Signal<boolean> = this._isAndroid.asReadonly();
    private readonly _isMobile: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isMobile: Signal<boolean> = this._isMobile.asReadonly();
    private readonly _isWebkit: WritableSignal<boolean> = signal<boolean>(false);
    // noinspection JSUnusedGlobalSymbols
    public readonly isWebkit: Signal<boolean> = this._isWebkit.asReadonly();
    private readonly _isSafari: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isSafari: Signal<boolean> = this._isSafari.asReadonly();
    private readonly _isChrome: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isChrome: Signal<boolean> = this._isChrome.asReadonly();
    private readonly _isIOSChrome: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isIOSChrome: Signal<boolean> = this._isIOSChrome.asReadonly();
    private readonly _isEdge: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isEdge: Signal<boolean> = this._isEdge.asReadonly();
    private readonly _isOpera: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isOpera: Signal<boolean> = this._isOpera.asReadonly();
    private readonly _isFirefox: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isFirefox: Signal<boolean> = this._isFirefox.asReadonly();
    private readonly _hasViewport: WritableSignal<boolean> = signal<boolean>(false);
    // noinspection JSUnusedGlobalSymbols
    public readonly hasViewport: Signal<boolean> = this._hasViewport.asReadonly();
    private readonly _forceMobile: WritableSignal<boolean> = signal<boolean>(false);
    public readonly forceMobile: Signal<boolean> = this._forceMobile.asReadonly();
    private readonly _isTouchDevice: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isTouchDevice: Signal<boolean> = this._isTouchDevice.asReadonly();
    private readonly _isKeyboardOpen: WritableSignal<boolean> = signal<boolean>(false);
    public readonly isKeyboardOpen: Signal<boolean> = this._isKeyboardOpen.asReadonly();
    private readonly _isDocumentElementReducedForKeyboard: WritableSignal<boolean> = signal<boolean>(false);
    // noinspection JSUnusedGlobalSymbols
    public readonly isDocumentElementReducedForKeyboard: Signal<boolean> = this._isDocumentElementReducedForKeyboard.asReadonly();
    private readonly _mode: WritableSignal<PageMode> = signal<PageMode>(undefined);
    public readonly mode: Signal<PageMode> = this._mode.asReadonly();
    private readonly _windowWidth: WritableSignal<number> = signal<number>(0);
    public readonly windowWidth: Signal<number> = this._windowWidth.asReadonly();
    private readonly _windowHeight: WritableSignal<number> = signal<number>(0);
    public readonly windowHeight: Signal<number> = this._windowHeight.asReadonly();
    private readonly _viewportWidth: WritableSignal<number> = signal<number>(undefined);
    // noinspection JSUnusedGlobalSymbols
    public readonly viewportWidth: Signal<number> = this._viewportWidth.asReadonly();
    private readonly _viewportHeight: WritableSignal<number> = signal<number>(undefined);
    // noinspection JSUnusedGlobalSymbols
    public readonly viewportHeight: Signal<number> = this._viewportHeight.asReadonly();
    private readonly _availableHeight: WritableSignal<number> = signal<number>(0);
    public readonly availableHeight: Signal<number> = this._availableHeight.asReadonly();

    public readonly short: Signal<boolean> = computed<boolean>(() => this._availableHeight() < 768);
    public readonly mobile: Signal<boolean> = computed<boolean>(() => this._mode() === PageMode.MOBILE || this._mode() === PageMode.MEDIUM_MOBILE || this._mode() === PageMode.SMALL_MOBILE);
    public readonly desktop: Signal<boolean> = computed<boolean>(() => this.mode() !== undefined && !this.mobile());

    public readonly calculateAvailableWidth: Signal<(nativeElement: HTMLElement, pillarboxed: "mobile" | "desktop" | "double-mobile" | "none") => number> = computed(() => {
        const windowWidth: number = this._windowWidth();
        return (nativeElement: HTMLElement, pillarboxed: "mobile" | "desktop" | "double-mobile" | "none"): number => {
            if (!pillarboxed || pillarboxed === "none") {
                return windowWidth;
            }
            const pillarboxWidthProperty: string = pillarboxed === "desktop" ? "--woa-desktop-pillarbox-width" : "--woa-full-page-pillarbox-width";
            let pillarboxWidth: number = parseInt(getComputedStyle(nativeElement).getPropertyValue(pillarboxWidthProperty).replace("px", ""));
            if (pillarboxed === "double-mobile") {
                pillarboxWidth *= 2;
            }
            return Math.min(pillarboxWidth, windowWidth);
        };
    });

    public readonly calculateMode: Signal<(desktopWidth: number) => PageMode> = computed(() => {
        const isSafari: boolean = this._isSafari();
        const isIos: boolean = this._isIos();
        const hasViewport: boolean = this._hasViewport();
        const forceMobileSignal: boolean = this._forceMobile();
        return (desktopWidth: number): PageMode => {
            const forceMobile: boolean = isSafari && isIos && !hasViewport && window.innerHeight < 650 || forceMobileSignal;
            return window.innerWidth < 375 ? PageMode.SMALL_MOBILE
                : window.innerWidth < 425 ? PageMode.MEDIUM_MOBILE
                    : forceMobile || window.innerWidth < 774 ? PageMode.MOBILE
                        : desktopWidth < 1024 ? PageMode.NARROW_DESKTOP
                            : desktopWidth < 1440 ? PageMode.BASIC_DESKTOP
                                : desktopWidth < 1920 ? PageMode.WIDE_DESKTOP
                                    : desktopWidth < 2560 ? PageMode.WIDER_DESKTOP
                                        : PageMode.WIDEST_DESKTOP;
        };
    });

    private lastViewportWidth: number;
    private lastViewportHeight: number;
    private lastWindowWidth: number;
    private lastWindowHeight: number;

    constructor() {
        console.log("--- ResponsiveService");

        void this.initConfig();

        /**
         * Watch the window for resize
         */
        onEach(fromEvent(window, "resize").pipe(
            debounceTime(1)
        ), () => this.handleWindowResize(false, true), this.destroyRef);

        if (window["visualViewport"]) {
            onEach(fromEvent(window["visualViewport"], "resize").pipe(
                debounceTime(1)
            ), () => this.handleViewportResize(), this.destroyRef);
        }

        onEach(fromEvent<KeyboardEvent>(document, "keydown"), (event: KeyboardEvent) => {
            if (event.shiftKey && event.ctrlKey && event.code === "KeyK") {
                if (this._isKeyboardOpen()) {
                    this.notifyKeyboardClose();
                } else {
                    this.notifyKeyboardOpen(398);
                }
            }
        }, this.destroyRef);
    }

    private async initConfig(): Promise<void> {
        const paramConfig: ParamConfig = await this.configurationModel.getParamConfig();

        this._forceMobile.set(paramConfig.forceMobile);
        this._isIpad.set(
            (/iPad/.test(navigator.userAgent) && !(window as any).MSStream) ||
            (/Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 2 && !/iPhone/.test(navigator.userAgent)));
        this._isIos.set((/iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream) || this._isIpad());
        this._isAndroid.set(/android/i.test(navigator.userAgent));
        this._isMobile.set(this._isIos() || this._isAndroid());
        this._isWebkit.set(/AppleWebKit/.test(navigator.userAgent) && !/Chrome|CriOS|Edge/.test(navigator.userAgent));
        this._isSafari.set(this._isWebkit() && /Safari/.test(navigator.userAgent));
        this._isIOSChrome.set(/CriOS/.test(navigator.userAgent));
        this._isEdge.set(/Edge/.test(navigator.userAgent));
        this._isOpera.set(/Edge/.test(navigator.userAgent));
        this._isChrome.set(/Chrome|Chromium/i.test(navigator.userAgent) && !this._isEdge() && !this._isOpera() && !this._isEdge());
        this._isFirefox.set(/Firefox/i.test(navigator.userAgent));
        this._hasViewport.set(!!window["visualViewport"]);
        this._isTouchDevice.set("ontouchstart" in window || navigator.maxTouchPoints > 0);

        this.updateDimensions();
        this.setModeToWindowWidth();
        this.lastWindowWidth = this._windowWidth();
        this.lastWindowHeight = this._windowHeight();
    }


    public setModeToWindowWidth(): void {
        this._mode.set(this.calculateMode()(this._windowWidth()));
    }

    private updateDimensions(): void {
        this._viewportWidth.set(this._hasViewport() ? window["visualViewport"].width : undefined);
        this._viewportHeight.set(this._hasViewport() ? window["visualViewport"].height : undefined);
        this._windowWidth.set(/* this._isSafari() && this._isIos() && */ this._hasViewport() ? Math.floor(this._viewportWidth()) : window.innerWidth);
        this._windowHeight.set(/* this._isSafari() && this._isIos() && */ this._hasViewport() ? Math.floor(this._viewportHeight()) : window.innerHeight);
        this._availableHeight.set(this._isDocumentElementReducedForKeyboard() || (this._isIos() && this._isKeyboardOpen()) ? document.documentElement.offsetHeight : this._windowHeight());
    }

    private handleViewportResize(): void {
        this.updateDimensions();
        console.log("Screen size: " + window.screen.width + "x" + window.screen.height);
        console.log("Viewport size: " + this._viewportWidth() + "x" + this._viewportHeight());

        // Issue with opening new tabs on IOS safari and chrome:
        // Depending on the timing of opening the app, the initial viewport size reported may be the full size of the screen,
        // which is shortly thereafter changed to reflect the actual available viewport size taking into account browser
        // headers and footers.
        // This can cause a false positive for detecting a keyboard open as the viewport height reduces.
        // To solve this, if the initial viewport dimensions match the full size of the screen, we don't attempt to infer
        // that the keyboard has opened.
        let suppressKeyboardChange: boolean = false;
        if (this._isMobile()) {
            suppressKeyboardChange = this.lastViewportWidth !== this._viewportWidth();
            if (!suppressKeyboardChange &&
                Math.max(this.lastViewportWidth, this.lastViewportHeight) === Math.max(window.screen.width, window.screen.height) &&
                Math.min(this.lastViewportWidth, this.lastViewportHeight) === Math.min(window.screen.width, window.screen.height)) {
                console.log("Previous viewport size matched full screen - not inferring keyboard transition");
                suppressKeyboardChange = true;
            }
        }
        this.lastViewportWidth = this._viewportWidth();
        this.lastViewportHeight = this._viewportHeight();

        this.handleWindowResize(suppressKeyboardChange, true);
    }

    private handleWindowResize(suppressKeyboardChange: boolean, deferOnKeyboardChange: boolean): void {
        this.updateDimensions();

        /**
         * We have to tell the application something has changed
         * because this is not part of the change detection that
         * Angular runs
         */
        this.applicationRef.tick();

        if (this.lastWindowWidth !== this._windowWidth() || this.lastWindowHeight !== this._windowHeight()) {
            console.log("Window size: " + this._windowWidth() + "x" + this._windowHeight());

            if (this.lastWindowHeight !== this._windowHeight()) {
                if (!suppressKeyboardChange && this._isMobile() && this.lastWindowWidth === this._windowWidth()) {

                    // Issue with opening new tabs on IOS safari and chrome:
                    // Depending on the timing of opening the app, the initial window size reported may be the full size of the screen,
                    // which is shortly thereafter changed to reflect the actual available window size taking into account browser
                    // headers and footers.
                    // This can cause a false positive for detecting a keyboard open as the window height reduces.
                    // To solve this, if the initial window dimensions match the full size of the screen, we don't attempt to infer
                    // that the keyboard has opened.

                    if (Math.max(this.lastWindowWidth, this.lastWindowHeight) === Math.max(window.screen.width, window.screen.height) &&
                        Math.min(this.lastWindowWidth, this.lastWindowHeight) === Math.min(window.screen.width, window.screen.height)) {
                        console.log("Previous window size matched full screen - not inferring keyboard transition");
                    } else {
                        if (this.detectMobileBrowserKeyboardOpenClose(deferOnKeyboardChange)) {
                            console.log("Detected potential keyboard open/close - deferring action");
                            setTimeout(() => this.handleWindowResize(false, false), 250);
                            return;
                        }
                    }
                }

                this.lastWindowHeight = this._windowHeight();
            }
            this.lastWindowWidth = this._windowWidth();
            this.setModeToWindowWidth();
        }

        document.documentElement.style.height = "100%";
        document.documentElement.style.maxHeight = "100%";
    }

    /**
     * Detect virtual keyboard open/close for mobile browser (native events used for apps)
     * @private
     */
    private detectMobileBrowserKeyboardOpenClose(defer: boolean): boolean {
        const diff = this._windowHeight() - this.lastWindowHeight;
        if (diff < -150) {
            if (defer) {
                return true;
            }
            this.notifyKeyboardOpen();
        }
        if (diff > 150) {
            if (defer) {
                return true;
            }
            this.notifyKeyboardClose();
        }
        return false;
    }

    private notifyKeyboardOpen(keyboardHeight: number = this.lastWindowHeight - this._windowHeight()): void {
        if (keyboardHeight <= 0) {
            return;
        }

        if (!this._isKeyboardOpen()) {
            console.log("Keyboard is open at height: " + keyboardHeight);
            this._isKeyboardOpen.set(true);
            document.body.classList.add("keyboard-is-open");
        }

        // Make sure the element with focus is visible
        if (document.activeElement) {
            setTimeout(() => {
                if (!this._isKeyboardOpen()) {
                    return;
                }
                this.scrollActiveElementIntoView();
            }, 500);
        }
    }

    private notifyKeyboardClose(): void {
        if (this._isKeyboardOpen()) {
            console.log("Keyboard is closed");
            this._isKeyboardOpen.set(false);
            document.body.classList.remove("keyboard-is-open");
            document.body.style.removeProperty("--document-scroll-top");
            document.body.style.removeProperty("--keyboard-open-window-height");

            // Restore the size of the document element
            if (this._isDocumentElementReducedForKeyboard()) {
                setTimeout(() => {
                    if (this._isKeyboardOpen()) {
                        console.log("Keyboard is reopened - not restoring document element height");
                    } else {
                        console.log("Restoring document element height");
                        document.documentElement.style.removeProperty("height");
                        this._isDocumentElementReducedForKeyboard.set(false);
                        setTimeout(() => {
                            this.setModeToWindowWidth();
                        });
                    }
                }, 100);
            }
        }
    }

    public scrollActiveElementIntoView(scrollBlock: ScrollLogicalPosition = "center") {
        // @ts-ignore
        if (document.activeElement.scrollIntoViewIfNeeded) {
            // @ts-ignore
            document.activeElement.scrollIntoViewIfNeeded(true);
        } else {
            const dist: number = this.getDistanceOffScreen(document.activeElement);
            if (dist) {
                document.activeElement.scrollIntoView({
                    block: scrollBlock,
                    inline: "nearest",
                    behavior: "smooth"
                });
                setTimeout(() => (<HTMLElement>document.activeElement).focus(), 500);
            }
        }
    }

    public getDistanceOffScreen(el: Element): number {
        const rect = el.getBoundingClientRect();
        if (rect.top < 0) {
            return rect.top;
        }
        if (rect.bottom > this._availableHeight()) {
            return rect.bottom - this._availableHeight();
        }
        return 0;
    }
}
